import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { createAssistantSession, chatWithAssistant } from "../api/client";

function useLocalDateFormatter() {
  const { i18n } = useTranslation();
  return React.useCallback(
    (value) => {
      if (!value) return "";
      const s = String(value).trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const loc = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";
        return d.toLocaleDateString(loc);
      }
      return s;
    },
    [i18n.language]
  );
}

const PlatformAiChatDialog = ({ open, onClose }) => {
  const { t } = useTranslation("shell");
  const fmtDate = useLocalDateFormatter();
  const [sessionId, setSessionId] = React.useState("");
  const [meta, setMeta] = React.useState(null);
  const [chatMessages, setChatMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const listRef = React.useRef(null);

  const sessionMutation = useMutation(async () => createAssistantSession({}), {
    onSuccess: (data) => {
      setSessionId(String(data?.sessionId || ""));
      setMeta(data || null);
      const hello = String(data?.message || "").trim();
      setChatMessages(hello ? [{ role: "assistant", content: hello }] : []);
    },
  });

  const chatMutation = useMutation(async ({ userMessage, history }) => {
    return chatWithAssistant({ sessionId, userMessage, history });
  });

  React.useEffect(() => {
    if (!open) return;
    setSessionId("");
    setMeta(null);
    setChatMessages([]);
    setInput("");
    sessionMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!listRef.current) return;
    try {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    } catch {}
  }, [chatMessages, chatMutation.isLoading]);

  const handleSend = async () => {
    const msg = String(input || "").trim();
    if (!msg || chatMutation.isLoading || !sessionId) return;
    const next = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(next);
    setInput("");

    try {
      const history = next.slice(-12);
      const resp = await chatMutation.mutateAsync({ userMessage: msg, history });
      const reply =
        String(resp?.reply || resp?.message || "").trim() || t("platformAi.replyFallback");
      setChatMessages((cur) => [...cur, { role: "assistant", content: reply }]);
    } catch (e) {
      const emsg =
        (typeof e?.message === "string" && e.message) ||
        (typeof e?.error === "string" && e.error) ||
        t("platformAi.chatError");
      setChatMessages((cur) => [...cur, { role: "assistant", content: emsg }]);
    }
  };

  const period = meta?.overview?.period || meta?.period || null;
  const projects = Array.isArray(meta?.overview?.projects) ? meta.overview.projects : [];
  const projectList =
    projects.length > 0
      ? `${projects.slice(0, 3).join(", ")}${projects.length > 3 ? "…" : ""}`
      : "";

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon fontSize="small" />
        {t("platformAi.title")}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: { xs: "78vh", md: "70vh" },
        }}
      >
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          {t("platformAi.disclaimer")}
        </Alert>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip size="small" label={t("platformAi.chipAggregated")} variant="outlined" />
          {period?.start && period?.end && (
            <Chip
              size="small"
              label={t("platformAi.chipPeriod", {
                start: fmtDate(period.start),
                end: fmtDate(period.end),
              })}
              variant="outlined"
            />
          )}
          {projects?.length > 0 && (
            <Chip
              size="small"
              label={t("platformAi.chipProjects", { list: projectList })}
              variant="outlined"
            />
          )}
        </Box>

        {sessionMutation.isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{t("platformAi.sessionLoading")}</Typography>
          </Box>
        )}

        {sessionMutation.isError && (
          <Alert severity="warning" variant="outlined">
            {t("platformAi.sessionError")}
          </Alert>
        )}

        <Divider sx={{ my: 0.5 }} />

        <Box
          ref={listRef}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
            flex: "1 1 auto",
            overflow: "auto",
            bgcolor: "background.paper",
          }}
        >
          {chatMessages.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {t("platformAi.emptyHint")}
              {"\n"}
              {t("platformAi.emptyExamples")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {chatMessages.map((m, idx) => (
                <Box key={idx} sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "92%" }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: m.role === "user" ? "primary.main" : "grey.100",
                      color: m.role === "user" ? "primary.contrastText" : "text.primary",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <Typography variant="body2">{m.content}</Typography>
                  </Box>
                </Box>
              ))}
              {chatMutation.isLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">{t("platformAi.typing")}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            pt: 0.5,
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
          }}
        >
          <TextField
            size="small"
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("platformAi.inputPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={!sessionId || sessionMutation.isLoading}
          />
          <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || chatMutation.isLoading || !sessionId}>
            <SendIcon />
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("platformAi.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlatformAiChatDialog;
